import React, { useMemo, useState } from "react";
import styled from "styled-components";
import {
  List,
  ListItem,
  ListItemText,
  Paper,
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  IconButton,
  Divider,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import {
  formatDate_12h,
  capitalize,
  detailsTabsBoxStyle,
  overviewListItemStyles,
} from "../../services/Utils";
import { Grid } from "@mui/material";
import { useQuery } from "react-query";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useTheme } from "@mui/material/styles";
import { QRCodeSVG } from 'qrcode.react';
import { getVehicleAuditData, getCertAuditData } from "../../services/vehicle";
import { getColumnsData } from "./VehicleAuditColumns";
import { UserNameFromUserId } from "../common/UserNameFromUserId";
import StatusActiveInactive from "../common/StatusActiveInactive";
import {
  getCustomerConfig,
  getCustomerLabel,
  isFeatureEnabled,
} from "../../services/customerConfig";



const VehicleDetailField = ({ label, value }) => {
  const displayValue =
    value !== undefined && value !== null && value !== "" ? value : "-";

  return (
    <VehicleDetailItem>
      <DetailLabel variant="caption">{label}</DetailLabel>

      <DetailValue variant="body1" fontWeight={600}>
        {displayValue}
      </DetailValue>
    </VehicleDetailItem>
  );
};

const VehicleDetailsCard = styled(Paper)`
  width: 100%;
  overflow: hidden;
  border-radius: 12px;
`;

const VehicleDetailsHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
`;

const VehicleDetailItem = styled(Box)`
  height: 100%;
  min-height: 76px;
  padding: 14px 16px;
  border-radius: 8px;
`;

const DetailLabel = styled(Typography)`
  margin-bottom: 5px;
`;

const DetailValue = styled(Typography)`
  word-break: break-word;
`;


const KeyTextWrapper = styled.p`
  margin: 0;
  padding: 0;
  word-break: break-all;
  min-width: 250px;
`;

const VehicleDetailsTab = ({ vehicleData, isLoading }) => {
  const theme = useTheme();
  const [copyText, setCopyText] = useState("Copy");
  const columns = getColumnsData();
  const {
    device: {
      serialNo,
      vehicleKey,
      active = false,
      serialNoCreatedAt = "",
      vehicleKeyUpdatedAt = "",
      updatedBy = "",
      vehicle = { vehicleCreationDate: "" , trimId: ""},
      customerId = "",
      certAvailable,
    } = {},
  } = vehicleData;
  const { overview = {} } = vehicleData;
  const { purchaseDetails = {} } = vehicleData;
  const { connectivity = {} } = vehicleData;

  // Get customer config once
  const customerConfig = useMemo(() => getCustomerConfig(), []);

  const {
    data: auditData = [],
    isLoading: auditDataIsLoading,
    refetch,
  } = useQuery(["vehicleAuditData", serialNo], getVehicleAuditData, {
    enabled: !!serialNo && isFeatureEnabled("showVehicleAuditLogs"),
    refetchOnWindowFocus: true,
  });

  const { data: certAuditData = [] } = useQuery(
    ["certAuditData", serialNo],
    getCertAuditData,
    {
      enabled: !!serialNo && isFeatureEnabled("showCertificateAuditLogs"),
    }
  );

  //refer this - helper functions
  const isMSILCustomer = () => {
    return localStorage.getItem("selectedCustomer") === "MSIL";
  };

  const isHDCustomer = () => {
    return localStorage.getItem("selectedCustomer") === "HD";
  };

  const isSKODACustomer = () => {
    return localStorage.getItem("selectedCustomer") === "SKODA";
  };

  if (isLoading) {
    return <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", margin: "10%" }}><div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", margin: "10%" }}><CircularProgress /></div></div>;
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopyText("Copied to Clipboard");
    setTimeout(() => {
      setCopyText("Copy");
    }, 1000);
  };

  const renderTextwithIcon = (text) => {
    return (
      <>
        <>{text}</>
        <Tooltip title={copyText}>
          <IconButton onClick={() => copyToClipboard(text)}>
            <ContentCopyIcon
              fontSize="small"
              sx={{
                paddingTop: "2px",
                color: theme.palette.primary["900"],
                ":hover": {
                  cursor: "pointer",
                },
              }}
            />
          </IconButton>
        </Tooltip>
      </>
    );
  };

  return (
    <Box {...detailsTabsBoxStyle}>
      <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 2 }}>
        <Grid size={{ xs: 2, sm: 4, md: 4, lg: 4 }}>
          <List>
            <ListItem {...overviewListItemStyles}>
              <ListItemText
                primary="Serial No"
                secondary={renderTextwithIcon(serialNo || "-")}
              />
            </ListItem>
            <ListItem {...overviewListItemStyles}>
              <ListItemText
                primary="Status"
                secondary={active ? "Active" : "Inactive"}
              />
            </ListItem>
            <ListItem {...overviewListItemStyles}>
              {isFeatureEnabled("showHUManufactured") && (
                <ListItemText
                  primary={getCustomerLabel("huManufactured")}
                  secondary={formatDate_12h(serialNoCreatedAt)}
                />
              )}
            </ListItem>
            <ListItem {...overviewListItemStyles}>
              <ListItemText primary="Customer" secondary={customerId || "-"} />
            </ListItem>
            <ListItem {...overviewListItemStyles}>
              <ListItemText
                primary="Certificate"
                secondary={certAvailable ? "Available" : "-"}
              />
            </ListItem>
          </List>
        </Grid>
        <Grid size={{ xs: 2, sm: 4, md: 4, lg: 4 }} >
          <List>
            <ListItem
              {...overviewListItemStyles}
              style={{ wordBreak: "break-all", whiteSpace: "normal" }}
            >
              <ListItemText
                primary="Vehicle Key"
                secondary={renderTextwithIcon(vehicleKey || "-")}
              />
            </ListItem>
            <ListItem {...overviewListItemStyles}>
              <ListItemText
                primary="Vehicle Manufactured"
                secondary={formatDate_12h(vehicle?.vehicleCreationDate)}
              />
            </ListItem>
            <ListItem {...overviewListItemStyles}>
              <ListItemText
                primary="Vehicle Key Mapped On"
                secondary={formatDate_12h(vehicleKeyUpdatedAt)}
              />
            </ListItem>
            {isFeatureEnabled("showFeaturesEnabled") && (
              <ListItem {...overviewListItemStyles}>
                {vehicleData?.features?.length ? (
                  vehicleData?.features.map((i, index) => (
                    <ListItemText
                      key={index}
                      primary="Features Enabled"
                      secondary={
                        i?.active
                          ? i?.feature?.featureName.toUpperCase() || "-"
                          : "-"
                      }
                    />
                  ))
                ) : (
                  <ListItemText primary="Features Enabled" secondary="None" />
                )}
              </ListItem>
            )}
          </List>
        </Grid>
        <Grid size={{ xs: 2, sm: 4, md: 4, lg: 4 }}>
          <List>
            {isFeatureEnabled("showVehiclePurchaseDate") && (
              <ListItem {...overviewListItemStyles}>
                <ListItemText
                  primary="Vehicle Purchase Date"
                  secondary={formatDate_12h(purchaseDetails?.purchaseDate)}
                />
              </ListItem>
            )}
            {isFeatureEnabled("showLastSeenAt") && (
              <ListItem {...overviewListItemStyles}>
                <ListItemText
                  primary="Last Seen At"
                  secondary={formatDate_12h(connectivity?.timestamp)}
                />
              </ListItem>
            )}
            <ListItem {...overviewListItemStyles}>
              <ListItemText
                primary="QR Code to Register in Companion App"
                secondary={<QRCodeSVG style={{ marginTop: '10px' }} value={`${vehicleKey}@${serialNo}`} />}
              />
            </ListItem>
          </List>
        </Grid>

       {vehicle && (vehicle.model || vehicle.trim) && (
        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
          <VehicleDetailsCard variant="outlined">
            <VehicleDetailsHeader>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Vehicle Details
                </Typography>

                <Typography variant="body2">
                  Model and Trim information
                </Typography>
              </Box>
            </VehicleDetailsHeader>

            <Divider />

            <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
              <Grid
                container
                spacing={2}
                sx={{
                  width: "100%",
                }}
              >
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <VehicleDetailField
                    label="Model Name"
                    value={vehicle?.model?.name}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <VehicleDetailField
                    label="Model Year"
                    value={vehicle?.model?.modelYear}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <VehicleDetailField
                    label="Vehicle Type"
                    value={
                      vehicle?.model?.vehicleType
                        ? capitalize(vehicle.model.vehicleType)
                        : "-"
                    }
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <VehicleDetailField
                    label="Region"
                    value={vehicle?.model?.region}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <VehicleDetailField
                    label="Trim Name"
                    value={vehicle?.trim?.name}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <VehicleDetailField
                    label="Engine"
                    value={vehicle?.trim?.engine}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <VehicleDetailField
                    label="Transmission"
                    value={vehicle?.trim?.transmission}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <VehicleDetailField
                    label="Drivetrain"
                    value={vehicle?.trim?.drivetrain}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <VehicleDetailField
                    label="Market"
                    value={vehicle?.model?.market}
                  />
                </Grid>
              </Grid>
            </Box>
          </VehicleDetailsCard>
        </Grid>
      )}

        {auditData?.length > 0 && (
          <Grid size={{ xs: 2, sm: 6, md: 12, lg: 12 }}>
            <Typography
              sx={{ marginBottom: "1%", textTransform: "uppercase" }}
              variant="body1"
            >
              Vehicle audit logs
            </Typography>

            <TableContainer component={Paper} title="Vehicle Audit Logs">
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell>Serial No.</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Created By</TableCell>
                    <TableCell>Created Date</TableCell>
                    <TableCell>Vehicle Key</TableCell>
                    <TableCell>Certificate</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditData.map((row) => (
                    <TableRow
                      key={row.auditId}
                    >
                      <TableCell component="th" scope="row">
                        {row.serialNo}
                      </TableCell>
                      <TableCell>{capitalize(row?.action)}</TableCell>
                      <TableCell>
                        <UserNameFromUserId value={row.createdBy} />
                      </TableCell>
                      <TableCell>{formatDate_12h(row?.createdAt)}</TableCell>
                      <TableCell>
                        <KeyTextWrapper>{row?.vehicleKey || "-"}</KeyTextWrapper>
                      </TableCell>
                      <TableCell>
                        {row.certAvailable ? "Available" : "-"}
                      </TableCell>
                      <TableCell>
                        <StatusActiveInactive active={row?.active} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}

        {isFeatureEnabled("showCertificateAuditLogs") &&
          certAuditData?.length > 0 && (
            <Grid size={{ xs: 2, sm: 6, md: 12, lg: 12 }}>
              <Typography
                sx={{ marginBottom: "1%", textTransform: "uppercase" }}
                variant="body1"
              >
                Certificate audit logs
              </Typography>

              <TableContainer component={Paper} title="Certificate Audit Logs">
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Serial No.</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Created By</TableCell>
                      <TableCell>Created Date</TableCell>
                      <TableCell>Certificate ID</TableCell>
                      <TableCell>Auto Regenerated</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {certAuditData.map((cert) => (
                      <TableRow
                        key={cert?.certificateAuditId}
                      >
                        <TableCell component="th" scope="row">
                          {cert?.serialNo}
                        </TableCell>
                        <TableCell>{capitalize(cert?.action)}</TableCell>
                        <TableCell>
                          <UserNameFromUserId value={cert?.createdBy} />
                        </TableCell>
                        <TableCell>{formatDate_12h(cert?.createdAt)}</TableCell>
                        <TableCell>
                          <KeyTextWrapper>{cert?.certificateId}</KeyTextWrapper>
                        </TableCell>
                        <TableCell>
                          {cert?.autoRegenerated ? "Yes" : "No"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}
      </Grid>
    </Box>
  );
};

export default VehicleDetailsTab;
